import { ActionModal } from '@kcftech/design-system';
import { IHardwareHealthNotificationSubscriptionResponse } from '@kcftech/prometheus-client';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useEditHardwareHealthNotificationSubscription } from 'custom_hooks/data_hooks/notification_subscriptions/useEditHardwareHealthNotificationSubscription';
import { FaultTypesInput } from 'pages/Desk/issues/fault_type/FaultTypesInput';
import { isKcfUser } from 'pages/Desk/issues/shared';
import { EditHardwareHealthNotificationSubscriptionFormData } from 'pages/Settings/NotificationSubscriptions/form/types';
import { Controller, useForm } from 'react-hook-form';

export type EditHardwareHealthNotificationSubscriptionModalProps = Readonly<{
    notificationSubscription: IHardwareHealthNotificationSubscriptionResponse;
    onClose: (saved: boolean) => void;
}>;

const responseToFormData = (
    response: IHardwareHealthNotificationSubscriptionResponse,
): EditHardwareHealthNotificationSubscriptionFormData => ({
    isSilenced: response.IsSilenced,
    allowInternalOnly: isKcfUser(response.User.UserApplicationRole),
    faultTypes: response.NotificationTriggers.map((t) => ({
        id: t.FaultType.Id,
        internalOnly: t.FaultType.InternalOnly,
        fullName: t.FaultType.FullName,
        name: t.FaultType.Name,
    })),
});

export function EditHardwareHealthNotificationSubscriptionModal({
    notificationSubscription,
    onClose,
}: EditHardwareHealthNotificationSubscriptionModalProps) {
    const { showToast } = useNotificationContext();

    const form = useForm<EditHardwareHealthNotificationSubscriptionFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: false,
        defaultValues: responseToFormData(notificationSubscription),
    });

    const {
        control,
        watch,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = form;

    const allowInternalOnly = watch('allowInternalOnly');

    const {
        mutate: editHardwareHealthNotificationSubscription,
        isPending: isEditingHardwareHealth,
    } = useEditHardwareHealthNotificationSubscription({
        onSuccess: () => {
            onClose(true);
            showToast({
                type: 'success',
                content: 'Successfully updated hardware health notification subscription',
            });
        },
        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update hardware health notification subscription',
            }),
    });

    const handleEdit = handleSubmit((formData) => {
        editHardwareHealthNotificationSubscription({
            id: notificationSubscription.Id,
            isSilenced: formData.isSilenced,
            // TODO: handle push notifications when enabled in the future
            triggers: Object.values(formData.faultTypes).map((type) => ({
                FaultTypeId: type.id,
                IsEmailEnabled: true,
            })),
        });
    });

    return (
        <ActionModal
            title="Edit Hardware Health Notification Subscription"
            actionable={
                (!isSubmitted || isValid) &&
                !(isValidating || isSubmitting || isEditingHardwareHealth)
            }
            onAction={handleEdit}
            onClose={() => onClose(false)}
            actionButtonText="Save"
            closeButtonText="Cancel"
            height={384}
        >
            <ValidatableLabel
                label="Fault Type Notifications"
                required
                error={errors.faultTypes}
            >
                <Controller
                    name="faultTypes"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <FaultTypesInput
                            issueMode="hardwareHealth"
                            faultTypeIds={value.map((item) => item.id)}
                            onChange={(event) => onChange(event)}
                            hideInternal={!allowInternalOnly}
                        />
                    )}
                />
            </ValidatableLabel>
        </ActionModal>
    );
}
